import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Grid, Tab } from '@mui/material';
import {
  Alert, TabContext, TabList, TabPanel,
} from '@mui/lab';
import { CommandPanels, withBaseEditor } from '../../BaseEditor';
import TPs from './tp';
import Files from '../../files';
import Payments from './payments';

const StyledTab = styled(Tab).attrs(({ visible }) => ({
  style: visible ? {} : { display: 'none' },
}))`
`;

const GeneralTPColumnTypes = {};

function EditorRenderer({
  getControl,
  data,
  dataSetter,
  currentUser,
  fields,
}) {
  const [tab, setTab] = useState('general');

  const onTypeChange = useCallback(
    (e, v) => {
      let newData = { type: v };
      if (v === 1) newData = ({ ...newData, multi_contract: false });
      if (v !== 2) {
        newData = ({ ...newData, session_decision_date: null, session_decision_number: null });
      }
      dataSetter(newData);
    },
    [dataSetter],
  );

  useEffect(
    () => {
      if (!data.id && !data.type) {
        onTypeChange(null, 1);
      }
    },
    [data.id, data.type, onTypeChange],
  );

  const onMultiContractChange = useCallback(
    (e, v) => {
      if (v) {
        dataSetter({
          multi_contract: v,
          payer: null,
          payer_id: null,
          kdb: null,
          kdb_id: null,
          is_default: false,
        });
      } else dataSetter({ multi_contract: v });
    },
    [dataSetter],
  );
  const onWarehouseChange = useCallback(
    (e, newRecs) => dataSetter({ warehouse_set: newRecs }),
    [dataSetter],
  );
  const currentOrg = useMemo(
    () => (currentUser.is_superuser ? data.org && (data.org.id || null) : currentUser.org.id),
    [currentUser.is_superuser, currentUser.org.id, data.org],
  );
  const orgFilter = useMemo(
    () => ({
      org: currentOrg,
    }),
    [currentOrg],
  );

  const landlordFilter = useMemo(
    () => ({
      ...orgFilter,
      is_landlord: true,
    }),
    [orgFilter],
  );
  return (
    <TabContext value={tab}>
      <TabList onChange={(e, v) => setTab(v)} textColor="primary" indicatorColor="primary">
        <Tab value="general" label="Загальне" />
        <StyledTab value="general_lands" label="Відомості про землю" visible={data.type === 1} />
        <StyledTab value="lands_lands" label="Відомості про землю" visible={data.type === 2} />
        <StyledTab value="lands_easement" label="Відомості про сервітут" visible={data.type === 5} />
        <StyledTab value="lands_realty" label="Відомості про нерухомість" visible={[3, 6].includes(data.type)} />
        <Tab value="payments" label="Платежі" disabled={!data.id} />
        <Tab value="files" label="Додані файли" />
      </TabList>
      <TabPanel value="general">
        <Grid container spacing={2}>
          <Grid item sm={data.type === 2 ? 3 : 6}>
            {getControl('number')}
          </Grid>
          <Grid item sm={3}>
            {getControl('date')}
          </Grid>
          <Grid item sm={3}>
            {getControl('date_to')}
          </Grid>
          {data.type === 2 && (
          <Grid item sm={3}>
            {getControl('registration_date')}
          </Grid>
          )}
          <Grid item sm={4}>
            {getControl('type', { onChange: onTypeChange })}
          </Grid>
          <Grid item sm={data.type === 2 ? 4 : 8}>
            {getControl('name')}
          </Grid>
          {data.type === 2 && (
          <Grid item sm={4}>
            {getControl('landlord', { filter: landlordFilter })}
          </Grid>
          )}

          {!data.multi_contract && (
            <>
              <Grid item sm={6}>
                {getControl('payer', { backend: '/api/catalog/payers/', filter: orgFilter })}
              </Grid>
              <Grid item sm={6}>
                {getControl('kdb', { backend: '/api/catalog/kdbs/' })}
              </Grid>
            </>
          )}
          <Grid item sm={12} style={{ display: 'flex' }}>
            {!data.multi_contract && getControl('is_default')}
            {data.type !== 1 && getControl('multi_contract', { onChange: onMultiContractChange })}
          </Grid>
          {currentUser.is_superuser && (
            <Grid item sm={12}>
              {getControl('org', {
                backend: '/api/catalog/orgs/',
              })}
            </Grid>
          )}
        </Grid>
        <TPs.GeneralTP
          data={data.warehouse_set}
          options={fields.warehouse_set}
          onChange={onWarehouseChange}
          overrideColumns={GeneralTPColumnTypes}
        />
        {data.contract_property && (
          <Alert severity="info">
            {data.contract_property}
          </Alert>
        )}
      </TabPanel>
      {data.type === 1 && (
        <TabPanel value="general_lands">
          <Grid container spacing={2}>
            <Grid item sm={6}>
              {getControl('date_to')}
            </Grid>
            <Grid item sm={6}>
              {getControl('percent')}
            </Grid>
            <Grid item sm={6}>
              {getControl('land_category', {
                backend: '/api/catalog/landcategories/',
              })}
            </Grid>
            <Grid item sm={6}>
              {getControl('region', {
                canErase: true,
                backend: '/api/catalog/regions/',
                filter: orgFilter,
              })}
            </Grid>
            <Grid item sm={6}>
              {getControl('square', { precision: 5 })}
            </Grid>
            <Grid item sm={6}>
              {getControl('price')}
            </Grid>
            <Grid item sm={12}>
              {getControl('in_area')}
            </Grid>
          </Grid>
        </TabPanel>
      )}
      {data.type === 2 && (
        <TabPanel value="lands_lands">
          <Grid container spacing={2}>
            <Grid item sm={12}>
              {getControl('estate_name')}
            </Grid>
            {/* <Grid item sm={3}> */}
            {/*  {getControl('land_category', { */}
            {/*    backend: '/api/catalog/landcategories/', */}
            {/*  })} */}
            {/* </Grid> */}
            <Grid item sm={3}>
              {getControl('session_decision_number')}
            </Grid>
            <Grid item sm={3}>
              {getControl('session_decision_date')}
            </Grid>
            <Grid item sm={6}>
              {getControl('region', {
                canErase: true,
                backend: '/api/catalog/regions/',
                filter: orgFilter,
              })}
            </Grid>
            <Grid item sm={12}>
              {getControl('in_area')}
            </Grid>
          </Grid>
          <TPs.RealEstatTP
            data={data.realestate_set}
            options={fields.realestate_set}
            onChange={(e, newRecs) => dataSetter({ realestate_set: newRecs })}
            mode="land"
          />
          {data.multi_contract && (
            <TPs.MultiplePayersTP
              data={data.contractrelation_set}
              options={fields.contractrelation_set}
              onChange={(e, newRecs) => dataSetter({ contractrelation_set: newRecs })}
              overrideColumns={GeneralTPColumnTypes}
            />
          )}
        </TabPanel>
      )}
      {[3, 6].includes(data.type) && (
        <TabPanel value="lands_realty">
          <Grid container spacing={2}>
            {/* <Grid item sm={6}> */}
            {/*  {getControl('realty', { */}
            {/*    backend: '/api/realty/realty/', */}
            {/*    filter: orgFilter, */}
            {/*  })} */}
            {/* </Grid> */}
            <Grid item sm={12}>
              {getControl('estate_name')}
            </Grid>
          </Grid>
          <TPs.RealEstatTP
            data={data.realestate_set}
            options={fields.realestate_set}
            onChange={(e, newRecs) => dataSetter({ realestate_set: newRecs })}
            mode="realty"
          />
          {data.multi_contract && (
            <TPs.MultiplePayersTP
              data={data.contractrelation_set}
              options={fields.contractrelation_set}
              onChange={(e, newRecs) => dataSetter({ contractrelation_set: newRecs })}
              overrideColumns={GeneralTPColumnTypes}
            />
          )}
        </TabPanel>
      )}
      {data.type === 5 && (
        <TabPanel value="lands_easement">
          <TPs.RealEstatTP
            data={data.realestate_set}
            options={fields.realestate_set}
            onChange={(e, newRecs) => dataSetter({ realestate_set: newRecs })}
            mode="easement"
          />
        </TabPanel>
      )}
      <TabPanel value="files">
        <Files model="contract" objectId={data.id} />
      </TabPanel>
      <TabPanel value="payments" sx={{ overflow: 'auto' }}>
        <Payments contractId={data.id} />
      </TabPanel>
    </TabContext>
  );
}

EditorRenderer.propTypes = {
  getControl: PropTypes.func.isRequired,
  data: PropTypes.shape({
    id: PropTypes.number,
    type: PropTypes.oneOf([1, 2, 3]),
    warehouse_set: PropTypes.arrayOf(PropTypes.shape({})),
    contractrelation_set: PropTypes.arrayOf(PropTypes.shape({})),
    realestate_set: PropTypes.arrayOf(PropTypes.shape({})),
    org: PropTypes.shape({
      id: PropTypes.number,
    }),
    multi_contract: PropTypes.bool,
    contract_property: PropTypes.string,
  }).isRequired,
  dataSetter: PropTypes.func.isRequired,
  currentUser: PropTypes.shape().isRequired,
  fields: PropTypes.shape({
    warehouse_set: PropTypes.shape({}),
    contractrelation_set: PropTypes.shape({}),
    realestate_set: PropTypes.shape({}),
  }).isRequired,
};

const CP = CommandPanels.docCommandPanel();
export default withBaseEditor(EditorRenderer, '/api/catalog/contracts/', CP);
